import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { ArbitrageOpportunity, OpportunityWithSnapshot } from '../types/opportunity';
import { makeOpportunityKey } from '../types/opportunity';
import { mergeOpportunityWithSnapshot } from '../utils/snapshotComparison';
import { useSelector } from 'react-redux';
import { selectSnapshots } from '../features/snapshots/snapshotsSlice';

interface OpportunitiesContextType {
  // Opportunities data
  opportunities: { [key: string]: ArbitrageOpportunity };
  sortedOpportunities: OpportunityWithSnapshot[];
  
  // Connection status
  isConnected: boolean;
  lastUpdate: number;
  error: string | null;
  
  // Actions
  setOpportunities: (opportunities: ArbitrageOpportunity[]) => void;
  bulkUpdateOpportunities: (update: BulkUpdate) => void;
  clearOpportunities: () => void;
  setConnectionStatus: (connected: boolean) => void;
  setError: (error: string | null) => void;
}

interface BulkUpdate {
  inserts: ArbitrageOpportunity[];
  updates: ArbitrageOpportunity[];
  deletes: string[];
}

const OpportunitiesContext = createContext<OpportunitiesContextType | undefined>(undefined);

export function OpportunitiesProvider({ children }: { children: React.ReactNode }) {
  // Local state for high-frequency data
  const [opportunities, setOpportunitiesState] = useState<{ [key: string]: ArbitrageOpportunity }>({});
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // Still get snapshots from Redux (low-frequency data)
  const snapshots = useSelector(selectSnapshots);
  
  // Use a ref to always have the latest snapshots available in callbacks
  const snapshotsRef = useRef(snapshots);
  useEffect(() => {
    snapshotsRef.current = snapshots;
  }, [snapshots]);
  
  // Set all opportunities (batch update from cache)
  const setOpportunities = useCallback((newOpportunities: ArbitrageOpportunity[]) => {
    setOpportunitiesState(prev => {
      const opportunitiesMap: { [key: string]: ArbitrageOpportunity } = {};
      
      // First, add all new opportunities
      newOpportunities.forEach(opp => {
        const key = makeOpportunityKey(opp);
        opportunitiesMap[key] = opp;
      });
      
      // IMPORTANT: Preserve starred opportunities that are missing from new data
      // Use ref to get the latest snapshots without causing re-renders
      const currentSnapshots = snapshotsRef.current;
      
      // Track if we preserved any starred items (for debugging)
      let preservedCount = 0;
      
      // Check each starred item and preserve it if missing from new data
      Object.entries(currentSnapshots).forEach(([starredKey, snapshot]) => {
        if (!opportunitiesMap[starredKey]) {
          // Starred item is missing from new data
          if (prev[starredKey]) {
            // We have it from before, preserve it
            opportunitiesMap[starredKey] = prev[starredKey];
            preservedCount++;
          } else if (snapshot && snapshot.opportunity) {
            // Use the opportunity data from the snapshot
            opportunitiesMap[starredKey] = snapshot.opportunity;
            preservedCount++;
          }
        }
      });
      
      // Remove verbose logging - this runs every 75ms
      
      return opportunitiesMap;
    });
    
    setLastUpdate(Date.now());
    setError(null);
  }, []);
  
  // Bulk update for batched operations (most efficient for high-frequency updates)
  const bulkUpdateOpportunities = useCallback((update: BulkUpdate) => {
    setOpportunitiesState(prev => {
      const updated = { ...prev };
      const currentSnapshots = snapshotsRef.current;
      
      // Process inserts and updates
      [...update.inserts, ...update.updates].forEach(opp => {
        const key = makeOpportunityKey(opp);
        updated[key] = opp;
      });
      
      // Process deletes (but NEVER delete starred items)
      update.deletes.forEach(key => {
        // Only delete if it's not starred
        if (!currentSnapshots[key]) {
          delete updated[key];
        }
      });
      
      return updated;
    });
    setLastUpdate(Date.now());
  }, []);
  
  // Clear all opportunities (but preserve starred ones)
  const clearOpportunities = useCallback(() => {
    setOpportunitiesState(prev => {
      const result: { [key: string]: ArbitrageOpportunity } = {};
      const currentSnapshots = snapshotsRef.current;
      
      // Only keep starred opportunities
      Object.keys(currentSnapshots).forEach(starredKey => {
        if (prev[starredKey]) {
          result[starredKey] = prev[starredKey];
        }
      });
      
      return result;
    });
    setLastUpdate(Date.now());
  }, []);
  
  // Set connection status
  const setConnectionStatus = useCallback((connected: boolean) => {
    setIsConnected(connected);
    if (!connected) {
      setError('Disconnected from server');
    } else {
      setError(null);
    }
  }, []);
  
  // Compute sorted opportunities with snapshots
  const sortedOpportunities = useMemo(() => {
    const result: OpportunityWithSnapshot[] = [];
    const processedKeys = new Set<string>();
    
    // First, add all starred/snapshot items (they go to the top)
    Object.entries(snapshots).forEach(([key, snapshot]) => {
      const currentOpp = opportunities[key];
      const merged = mergeOpportunityWithSnapshot(currentOpp, snapshot);
      if (merged) {
        result.push(merged);
        processedKeys.add(key);
      }
    });
    
    // Then add all non-starred opportunities
    Object.entries(opportunities).forEach(([key, opportunity]) => {
      if (!processedKeys.has(key)) {
        const merged = mergeOpportunityWithSnapshot(opportunity, undefined);
        if (merged) {
          result.push(merged);
        }
      }
    });
    
    // Sort the results
    return result.sort((a, b) => {
      // First sort by starred status (starred items at top)
      if (a.isStarred && !b.isStarred) return -1;
      if (!a.isStarred && b.isStarred) return 1;
      
      // Within each group, sort by profit ratio (highest first)
      const aProfitRatio = a.profit_ratio || 1.0;
      const bProfitRatio = b.profit_ratio || 1.0;
      
      const profitDiff = bProfitRatio - aProfitRatio;
      if (profitDiff !== 0) return profitDiff;
      
      // Then by detected time (newest first)
      return b.detected_at - a.detected_at;
    });
  }, [opportunities, snapshots]);
  
  const value = useMemo(() => ({
    opportunities,
    sortedOpportunities,
    isConnected,
    lastUpdate,
    error,
    setOpportunities,
    bulkUpdateOpportunities,
    clearOpportunities,
    setConnectionStatus,
    setError,
  }), [
    opportunities,
    sortedOpportunities,
    isConnected,
    lastUpdate,
    error,
    setOpportunities,
    bulkUpdateOpportunities,
    clearOpportunities,
    setConnectionStatus,
  ]);
  
  return (
    <OpportunitiesContext.Provider value={value}>
      {children}
    </OpportunitiesContext.Provider>
  );
}

export function useOpportunities() {
  const context = useContext(OpportunitiesContext);
  if (!context) {
    throw new Error('useOpportunities must be used within an OpportunitiesProvider');
  }
  return context;
}